import { lazy, Suspense, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ProfileProvider } from "@/contexts/ProfileContext";
import { LogoSizeProvider } from "@/contexts/LogoSizeContext";
import LogoSizerPanel from "@/components/dev/LogoSizerPanel";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import ImpersonationBanner from "@/components/ImpersonationBanner";

// Eagerly load only the homepage so first paint stays flicker-free. Every
// other route is code-split via React.lazy so the initial JS payload on `/`
// is dramatically smaller (was bundling all ~70 pages into one chunk,
// causing ~2.2s of script evaluation per Lighthouse).
//
// SAFE: this is a build-time bundling change only. JSX, props, route paths,
// auth gates, and rendered output are unchanged. The Suspense fallback below
// is `null` so route transitions do not flash a spinner - identical UX to
// the previous eager-import behaviour from the user's perspective (just with
// a brief network fetch on first navigation to a route).
import Index from "./pages/Index.tsx";
import NotFound from "./pages/NotFound.tsx";

const HowItWorks = lazy(() => import("./pages/HowItWorks.tsx"));
const Dashboard = lazy(() => import("./pages/Dashboard.tsx"));
const MyReviews = lazy(() => import("./pages/MyReviews.tsx"));
const Login = lazy(() => import("./pages/Login.tsx"));
const SignUp = lazy(() => import("./pages/SignUp.tsx"));
const VetSignUp = lazy(() => import("./pages/VetSignUp.tsx"));
const VetSignUpConfirmation = lazy(() => import("./pages/VetSignUpConfirmation.tsx"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
const AddPet = lazy(() => import("./pages/AddPet.tsx"));
const EditPet = lazy(() => import("./pages/EditPet.tsx"));
const PetPassport = lazy(() => import("./pages/PetPassport.tsx"));
const PetPassportsIndex = lazy(() => import("./pages/PetPassportsIndex.tsx"));
const PassportShareViewer = lazy(() => import("./pages/PassportShareViewer.tsx"));
const CalendarView = lazy(() => import("./pages/CalendarView.tsx"));
const BookAppointment = lazy(() => import("./pages/BookAppointment.tsx"));
const VetDashboard = lazy(() => import("./pages/VetDashboard.tsx"));
const VetSnapshot = lazy(() => import("./pages/VetSnapshot.tsx"));
const BadgeProgressDashboard = lazy(() => import("./pages/BadgeProgressDashboard.tsx"));
const VetSubscribe = lazy(() => import("./pages/VetSubscribe.tsx"));
const VetProfile = lazy(() => import("./pages/VetProfile.tsx"));
const About = lazy(() => import("./pages/About.tsx"));
const Accessibility = lazy(() => import("./pages/Accessibility.tsx"));
const Cma = lazy(() => import("./pages/Cma.tsx"));
const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const CodeOfConduct = lazy(() => import("./pages/CodeOfConduct.tsx"));
const NoAbusePolicy = lazy(() => import("./pages/NoAbusePolicy.tsx"));
const AntiSlavery = lazy(() => import("./pages/AntiSlavery.tsx"));
const Cookies = lazy(() => import("./pages/Cookies.tsx"));
const Press = lazy(() => import("./pages/Press.tsx"));
const News = lazy(() => import("./pages/News.tsx"));
const NewsArticle = lazy(() => import("./pages/NewsArticle.tsx"));
const Blog = lazy(() => import("./pages/Blog.tsx"));
const BlogPost = lazy(() => import("./pages/BlogPost.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const SymptomSniffer = lazy(() => import("./pages/SymptomSniffer.tsx"));
const ForVets = lazy(() => import("./pages/ForVets.tsx"));
const VetDirectory = lazy(() => import("./pages/VetDirectory.tsx"));
const VetMap = lazy(() => import("./pages/VetMap.tsx"));
const Health = lazy(() => import("./pages/Health.tsx"));
const AdminBadges = lazy(() => import("./pages/AdminBadges.tsx"));
const AdminOwnerBadges = lazy(() => import("./pages/AdminOwnerBadges.tsx"));
const AdminReferrals = lazy(() => import("./pages/AdminReferrals.tsx"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard.tsx"));

const AdminSecurityFindings = lazy(() => import("./pages/AdminSecurityFindings.tsx"));
const AdminAuditLog = lazy(() => import("./pages/AdminAuditLog.tsx"));
const AdminCronHealth = lazy(() => import("./pages/AdminCronHealth.tsx"));
const AdminErrors = lazy(() => import("./pages/AdminErrors.tsx"));
const AdminCosts = lazy(() => import("./pages/AdminCosts.tsx"));
const AdminSlowQueries = lazy(() => import("./pages/AdminSlowQueries.tsx"));
const AdminSeo = lazy(() => import("./pages/AdminSeo.tsx"));
const Settings = lazy(() => import("./pages/Settings.tsx"));
const Notifications = lazy(() => import("./pages/Notifications.tsx"));
const AuthCallback = lazy(() => import("./pages/AuthCallback.tsx"));
const MfaChallenge = lazy(() => import("./pages/MfaChallenge.tsx"));
const Helpdesk = lazy(() => import("./pages/Helpdesk.tsx"));
const Messages = lazy(() => import("./pages/Messages.tsx"));
const HelpdeskArticle = lazy(() => import("./pages/HelpdeskArticle.tsx"));
const AdminHelpdesk = lazy(() => import("./pages/AdminHelpdesk.tsx"));
const AdminMaintenance = lazy(() => import("./pages/AdminMaintenance.tsx"));

const AdminVetAvailability = lazy(() => import("./pages/AdminVetAvailability.tsx"));
const AdminAvailabilityAudit = lazy(() => import("./pages/AdminAvailabilityAudit.tsx"));
const AdminImpersonationLog = lazy(() => import("./pages/AdminImpersonationLog.tsx"));
const AdminSuspiciousSignups = lazy(() => import("./pages/AdminSuspiciousSignups.tsx"));
const AdminVetOfflineEvents = lazy(() => import("./pages/AdminVetOfflineEvents.tsx"));
const AdminConsultationRecordings = lazy(() => import("./pages/AdminConsultationRecordings.tsx"));
const AdminTopRated = lazy(() => import("./pages/AdminTopRated.tsx"));
const AdminFeatured = lazy(() => import("./pages/AdminFeatured.tsx"));
const AdminMicrochipLookups = lazy(() => import("./pages/AdminMicrochipLookups.tsx"));
const AdminApiUsage = lazy(() => import("./pages/AdminApiUsage.tsx"));
const AdminAlertRules = lazy(() => import("./pages/AdminAlertRules.tsx"));
const AdminGoogleAudit = lazy(() => import("./pages/AdminGoogleAudit.tsx"));
const AdminCheckoutLogs = lazy(() => import("./pages/AdminCheckoutLogs.tsx"));
const AdminMessageAudit = lazy(() => import("./pages/AdminMessageAudit.tsx"));
const AdminMessagingHealth = lazy(() => import("./pages/AdminMessagingHealth.tsx"));
const AdminExportQa = lazy(() => import("./pages/AdminExportQa.tsx"));
const AdminNews = lazy(() => import("./pages/AdminNews.tsx"));
const AdminAiUsage = lazy(() => import("./pages/AdminAiUsage.tsx"));
const AdminFeatureFlags = lazy(() => import("./pages/AdminFeatureFlags.tsx"));
const AdminNewsletter = lazy(() => import("./pages/AdminNewsletter.tsx"));
const AdminPracticeClaims = lazy(() => import("./pages/AdminPracticeClaims.tsx"));
const AdminPendingVets = lazy(() => import("./pages/AdminPendingVets.tsx"));
const VetPending = lazy(() => import("./pages/VetPending.tsx"));
const Consultation = lazy(() => import("./pages/Consultation.tsx"));
const GetVideoVet = lazy(() => import("./pages/GetVideoVet.tsx"));
const EmergencyVet = lazy(() => import("./pages/EmergencyVet.tsx"));
const EmergencyVetCity = lazy(() => import("./pages/EmergencyVetCity.tsx"));
const PetFoodSafety = lazy(() => import("./pages/PetFoodSafety.tsx"));
const PetFoodSafetyItem = lazy(() => import("./pages/PetFoodSafetyItem.tsx"));
const GetPetPassport = lazy(() => import("./pages/GetPetPassport.tsx"));
const Security = lazy(() => import("./pages/Security.tsx"));
const Status = lazy(() => import("./pages/Status.tsx"));
const VetSecurity = lazy(() => import("./pages/VetSecurity.tsx"));
const ConfirmVetCustomer = lazy(() => import("./pages/ConfirmVetCustomer.tsx"));
const VetProfilePublic = lazy(() => import("./pages/VetProfilePublic.tsx"));
const ComparePostcode = lazy(() => import("./pages/ComparePostcode.tsx"));
const ComparePricesLanding = lazy(() => import("./pages/ComparePricesLanding.tsx"));
const CompareVets = lazy(() => import("./pages/CompareVets.tsx"));
const PriceComparison = lazy(() => import("./pages/PriceComparison.tsx"));
const VetsInCity = lazy(() => import("./pages/VetsInCity.tsx"));
const VetsInCityProcedure = lazy(() => import("./pages/VetsInCityProcedure.tsx"));
const CompleteProfile = lazy(() => import("./pages/CompleteProfile.tsx"));
const NewsletterConfirm = lazy(() => import("./pages/NewsletterConfirm.tsx"));

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const VetProfileResolver = () => {
  const { id } = useParams<{ id: string }>();
  return id && UUID_RE.test(id) ? <VetProfile /> : <VetProfilePublic />;
};

import RequireMfa from "./components/RequireMfa";
import RequireCompleteProfile from "./components/RequireCompleteProfile";
import ScrollToTop from "./components/ScrollToTop";
import AnalyticsRouteTracker from "./components/AnalyticsRouteTracker";
import RouteRobots from "./components/RouteRobots";
import CookieConsent from "./components/CookieConsent";
// Floating widgets are below-the-fold UI; lazy-load them so the homepage
// doesn't ship their code (and their downstream chat/markdown deps) on first paint.
const SymptomSnifferWidget = lazy(() => import("./components/SymptomSnifferWidget"));
const VetCopilotWidget = lazy(() => import("./components/VetCopilotWidget"));
const PwaInstallPrompt = lazy(() => import("./components/PwaInstallPrompt"));
const ExitIntentPostcode = lazy(() => import("./components/ExitIntentPostcode"));
const ScrollDepthSnifferPrompt = lazy(() => import("./components/ScrollDepthSnifferPrompt"));
import MaintenanceGate from "./components/MaintenanceGate";
import MfaStatusBanner from "./components/MfaStatusBanner";
import { IncomingConsultationListener } from "./components/consultation/IncomingConsultationListener";
import { PoppedConversationsProvider } from "./contexts/PoppedConversationsContext";
import { PoppedConversationsDock } from "./components/messages/PoppedConversationsDock";
import { useVetHeartbeat } from "./hooks/useVetHeartbeat";
import ReferralCaptureBoundary from "./components/ReferralCaptureBoundary";
import MobileBottomNav from "./components/MobileBottomNav";
import MobileBookCTA from "./components/MobileBookCTA";
import CompareFloatingBar from "./components/CompareFloatingBar";
import { CompareProvider } from "./contexts/CompareContext";
const PetOnboardingWizard = lazy(() => import("./pages/PetOnboardingWizard.tsx"));
const CoOwnerAccept = lazy(() => import("./pages/CoOwnerAccept.tsx"));

// Keeps vet_availability_now.last_heartbeat_at fresh for any logged-in vet
// regardless of route, so the 5-minute server-side stale-vet reaper doesn't
// flip them offline while they're browsing other pages.
const VetHeartbeatRunner = () => {
  useVetHeartbeat();
  return null;
};

// Mount the floating widgets (Symptom Sniffer, Vet Copilot) only after the
// browser is idle. Both pull in heavy downstream deps (stripe, markdown,
// forms) that were appearing on the homepage's critical request chain in
// Lighthouse. Deferring until idle keeps them out of FCP/LCP without changing
// behaviour - the FABs still appear once first paint settles.
const DeferredWidgets = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(() => setReady(true), { timeout: 3000 });
      return () => {
        const cancel = (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback;
        if (typeof cancel === "function") cancel(id);
      };
    }
    const t = window.setTimeout(() => setReady(true), 2000);
    return () => window.clearTimeout(t);
  }, []);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <SymptomSnifferWidget />
      <VetCopilotWidget />
    </Suspense>
  );
};

// Legacy /find-vets route - redirect to landing page (which now hosts inline search),
// preserving any query string so deep links like /find-vets?pet=cat still work.
const FindVetsRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/${location.search}`} replace state={{ autoSearch: true }} />;
};

const queryClient = new QueryClient();

const App = () => (
  <AppErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <ThemeProvider>
      <ProfileProvider>
      <LogoSizeProvider>
      <PoppedConversationsProvider>
      <CompareProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <AnalyticsRouteTracker />
          <ReferralCaptureBoundary />
          <RouteRobots />
          <RequireCompleteProfile />
          <MaintenanceGate>
              <MfaStatusBanner />
              <ImpersonationBanner />
          <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/my-dashboard" element={<Dashboard />} />
            <Route path="/my-dashboard/snapshot" element={<Navigate to="/my-dashboard" replace />} />
            <Route path="/my-dashboard/awards" element={<BadgeProgressDashboard />} />
            <Route path="/my-reviews" element={<MyReviews />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/complete-profile" element={<CompleteProfile />} />
            <Route path="/vet-signup" element={<VetSignUp />} />
            <Route path="/vet-signup/confirmation" element={<VetSignUpConfirmation />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/add-pet" element={<AddPet />} />
            <Route path="/edit-pet/:id" element={<EditPet />} />
            <Route path="/my-passports" element={<PetPassportsIndex />} />
            <Route path="/pet-passports" element={<Navigate to="/my-passports" replace />} />
            <Route path="/my-pets" element={<Navigate to="/my-dashboard" replace />} />
            <Route path="/pet/:id/passport" element={<PetPassport />} />
            <Route path="/passport/share/:token" element={<PassportShareViewer />} />
            <Route path="/find-vets" element={<FindVetsRedirect />} />
            <Route path="/vet-directory" element={<VetDirectory />} />
            <Route path="/vet-map" element={<VetMap />} />
            <Route path="/calendar" element={<CalendarView />} />
            <Route path="/book-appointment" element={<BookAppointment />} />
            <Route path="/consultation/:id" element={<Consultation />} />
            <Route path="/getvideovet" element={<GetVideoVet />} />
            <Route path="/online-vet" element={<Navigate to="/getvideovet" replace />} />
            <Route path="/emergency-vet" element={<EmergencyVet />} />
            <Route path="/emergency-vet/:city" element={<EmergencyVetCity />} />
            <Route path="/newsletter/confirm" element={<NewsletterConfirm />} />
            <Route path="/pet-food-safety" element={<PetFoodSafety />} />
            <Route path="/pet-food-safety/:slug" element={<PetFoodSafetyItem />} />
            <Route path="/getpetpassport" element={<GetPetPassport />} />
            <Route path="/security" element={<Security />} />
            <Route path="/status" element={<Status />} />
            <Route path="/vet-dashboard" element={<RequireMfa><VetDashboard /></RequireMfa>} />
            <Route path="/vet-dashboard/snapshot" element={<RequireMfa><VetSnapshot /></RequireMfa>} />
            <Route path="/vet-subscribe" element={<VetSubscribe />} />
            <Route path="/vet/:id" element={<VetProfileResolver />} />
            <Route path="/vets-in/:city" element={<VetsInCity />} />
            <Route path="/vets-in/:city/:procedure" element={<VetsInCityProcedure />} />
            <Route path="/compare" element={<ComparePricesLanding />} />
            <Route path="/compare-vets" element={<CompareVets />} />
            <Route path="/compare/:postcode" element={<ComparePostcode />} />
            <Route path="/prices/:procedure" element={<PriceComparison />} />
            <Route path="/about" element={<About />} />
            <Route path="/accessibility" element={<Accessibility />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/code-of-conduct" element={<CodeOfConduct />} />
            <Route path="/no-abuse-policy" element={<NoAbusePolicy />} />
            <Route path="/anti-slavery-policy" element={<AntiSlavery />} />
            <Route path="/cookies" element={<Cookies />} />
            
            <Route path="/press" element={<Press />} />
            <Route path="/cma" element={<Cma />} />
            <Route path="/news" element={<News />} />
            <Route path="/news/:slug" element={<NewsArticle />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/contact-us" element={<Contact />} />
            <Route path="/symptom-sniffer" element={<SymptomSniffer />} />
            <Route path="/for-vets" element={<ForVets />} />
            <Route path="/health" element={<Health />} />
            <Route path="/admin" element={<RequireMfa><AdminDashboard /></RequireMfa>} />
            <Route path="/admin/badges" element={<RequireMfa><AdminBadges /></RequireMfa>} />
            <Route path="/admin/owner-badges" element={<RequireMfa><AdminOwnerBadges /></RequireMfa>} />
            <Route path="/admin/referrals" element={<RequireMfa><AdminReferrals /></RequireMfa>} />
            <Route path="/admin/seo" element={<RequireMfa><AdminSeo /></RequireMfa>} />
            <Route path="/admin/mfa" element={<Navigate to="/admin/security?tab=mfa" replace />} />
            <Route path="/admin/security" element={<RequireMfa><AdminSecurityFindings /></RequireMfa>} />
            <Route path="/admin/audit-log" element={<RequireMfa><AdminAuditLog /></RequireMfa>} />
            <Route path="/admin/maintenance" element={<RequireMfa><AdminMaintenance /></RequireMfa>} />
            <Route path="/admin/export-qa" element={<RequireMfa><AdminExportQa /></RequireMfa>} />
            <Route path="/admin/auto-unpublished" element={<Navigate to="/admin/vet-availability?tab=offline" replace />} />
            <Route path="/admin/vet-availability" element={<RequireMfa><AdminVetAvailability /></RequireMfa>} />
            <Route path="/admin/availability-audit" element={<RequireMfa><AdminAvailabilityAudit /></RequireMfa>} />
            <Route path="/admin/impersonation-log" element={<RequireMfa><AdminImpersonationLog /></RequireMfa>} />
            <Route path="/admin/suspicious-signups" element={<RequireMfa><AdminSuspiciousSignups /></RequireMfa>} />
            <Route path="/admin/vet-offline-events" element={<RequireMfa><AdminVetOfflineEvents /></RequireMfa>} />
            <Route path="/admin/top-rated" element={<RequireMfa><AdminTopRated /></RequireMfa>} />
            <Route path="/admin/featured" element={<RequireMfa><AdminFeatured /></RequireMfa>} />
            <Route path="/admin/consultation-recordings" element={<RequireMfa><AdminConsultationRecordings /></RequireMfa>} />
            <Route path="/admin/microchip-lookups" element={<RequireMfa><AdminMicrochipLookups /></RequireMfa>} />
            <Route path="/admin/message-audit" element={<RequireMfa><AdminMessageAudit /></RequireMfa>} />
            <Route path="/admin/api-usage" element={<RequireMfa><AdminApiUsage /></RequireMfa>} />
            <Route path="/admin/alert-rules" element={<RequireMfa><AdminAlertRules /></RequireMfa>} />
            <Route path="/admin/checkout-logs" element={<RequireMfa><AdminCheckoutLogs /></RequireMfa>} />
            <Route path="/admin/google-audit" element={<RequireMfa><AdminGoogleAudit /></RequireMfa>} />
            <Route path="/admin/messaging-health" element={<RequireMfa><AdminMessagingHealth /></RequireMfa>} />
            <Route path="/admin/news" element={<RequireMfa><AdminNews /></RequireMfa>} />
            <Route path="/admin/ai-usage" element={<RequireMfa><AdminAiUsage /></RequireMfa>} />
            <Route path="/admin/feature-flags" element={<RequireMfa><AdminFeatureFlags /></RequireMfa>} />
            <Route path="/admin/newsletter" element={<RequireMfa><AdminNewsletter /></RequireMfa>} />
            <Route path="/admin/practice-claims" element={<RequireMfa><AdminPracticeClaims /></RequireMfa>} />
            <Route path="/admin/pending-vets" element={<RequireMfa><AdminPendingVets /></RequireMfa>} />
            <Route path="/vet-pending" element={<VetPending />} />
            <Route path="/admin/cron-health" element={<RequireMfa><AdminCronHealth /></RequireMfa>} />
            <Route path="/admin/errors" element={<RequireMfa><AdminErrors /></RequireMfa>} />
            <Route path="/admin/costs" element={<RequireMfa><AdminCosts /></RequireMfa>} />
            <Route path="/admin/slow-queries" element={<RequireMfa><AdminSlowQueries /></RequireMfa>} />

            <Route path="/settings" element={<Settings />} />
            <Route path="/settings/security" element={<VetSecurity />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/mfa" element={<MfaChallenge />} />
            <Route path="/helpdesk" element={<Helpdesk />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/helpdesk/:slug" element={<HelpdeskArticle />} />
            <Route path="/admin/helpdesk" element={<RequireMfa><AdminHelpdesk /></RequireMfa>} />
            <Route path="/confirm-vet-customer" element={<ConfirmVetCustomer />} />
            <Route path="/onboard-pet" element={<PetOnboardingWizard />} />
            <Route path="/co-owner/accept" element={<CoOwnerAccept />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          <DeferredWidgets />
          <IncomingConsultationListener />
          <VetHeartbeatRunner />
          </MaintenanceGate>
          <PoppedConversationsDock />
          <CookieConsent />
          <Suspense fallback={null}><PwaInstallPrompt /></Suspense>
          <Suspense fallback={null}><ExitIntentPostcode /></Suspense>
          <Suspense fallback={null}><ScrollDepthSnifferPrompt /></Suspense>
          <CompareFloatingBar />
          <MobileBookCTA />
          <MobileBottomNav />
          <LogoSizerPanel />
        </BrowserRouter>
      </TooltipProvider>
      </CompareProvider>
      </PoppedConversationsProvider>
      </LogoSizeProvider>
      </ProfileProvider>
      </ThemeProvider>
    </AuthProvider>
  </QueryClientProvider>
  </AppErrorBoundary>
);

export default App;
